"use client";

import { useEffect, useRef } from "react";

/**
 * Forget filter values whose entity is gone.
 *
 * URL-backed filters outlive the things they point at: a stage or a sequence is
 * deleted, a sequence is shortened past the step someone filtered on, or a link
 * is shared long after the fact. The value keeps narrowing the list server-side
 * while the panel — which only draws the options that exist — has no control to
 * explain or undo it. So it is dropped from the state, and from the URL.
 *
 * Two rules make this safe:
 *
 * 1. `offered` must be every value that *exists*, not the subset a panel feels
 *    like showing. Dropping a filter widens the result set, so pruning a value
 *    that is merely hidden would silently show rows the filter excluded.
 * 2. Pass `offered: null` while that set is unknown — mid-load, or after a
 *    failed request. Pruning against a list that hasn't arrived would throw away
 *    a shared link's filters on the way in.
 *
 * Call this where the list lives rather than inside the filter panel: a drawer
 * that is closed still isn't mounted, and a stale filter has to be dropped
 * whether or not anyone is looking at the control for it.
 *
 * @param fields  The filter keys to check, each with its selected values. Keys
 *                that share one `offered` set belong in one call, so both sides
 *                of an include/exclude pair are rewritten together.
 * @param offered Every value that exists, or `null` while unknown.
 * @param prune   Applies the patch — the panel's setter.
 */
export function usePruneMissingFilterValues<
  K extends string,
  V extends string | number,
>(
  fields: Record<K, V[]>,
  offered: readonly V[] | null,
  prune: (patch: Partial<Record<K, V[]>>) => void,
): void {
  const pruneRef = useRef(prune);
  useEffect(() => {
    pruneRef.current = prune;
  }, [prune]);

  // Derived during render, so the effect below depends on what the patch *is*
  // rather than on array identities that change every render.
  let patch: Partial<Record<K, V[]>> | null = null;
  if (offered !== null) {
    const exists = new Set<V>(offered);
    for (const key of Object.keys(fields) as K[]) {
      const selected = fields[key];
      const kept = selected.filter((value) => exists.has(value));
      if (kept.length !== selected.length) {
        patch ??= {};
        patch[key] = kept;
      }
    }
  }
  // JSON so the effect re-runs on a change of content, not of reference, and so
  // numbers survive the round trip as numbers.
  const encoded = patch === null ? null : JSON.stringify(patch);

  useEffect(() => {
    if (encoded === null) return;
    pruneRef.current(JSON.parse(encoded) as Partial<Record<K, V[]>>);
  }, [encoded]);
}
